import React, { useEffect, useRef } from "react";
import { useDesign } from "../../context/contextDesign";
import { useThemeMode } from "../../context/contextTheme";
import { useAuth } from "../../context/contextAuth";
import { useWeb } from "../../context/Dashboard/contextWeb.js";
import Loader from "../../components/loaders/Loader-4";
import ScanView from "../../layouts/web/ScanView.jsx";
import ScanCard from "../../layouts/web/ScanCard.jsx";

const WebDashboard = () => {
  const { theme } = useThemeMode();
  const { userId } = useAuth();
  const { bHeightVal } = useDesign();
  const {
    initData,
    loadWebData,
    webLoading,
    webResults,
    currentIndex,
    setCurrentIndex,
    urlScanLoading,
    scanJob,
    urlDashboardView,
    setUrlDashboardView,
    setUrlHeader
  } = useWeb();
  const initializedUserIdRef = useRef(null);

  // The main dashboard also initializes this context, but a browser refresh on
  // the Web Dashboard route mounts this page directly. Load its saved reports
  // here as soon as the restored authenticated user is available.
  useEffect(() => {
    if (!userId) {
      initializedUserIdRef.current = null;
      return;
    }

    if (initializedUserIdRef.current === userId) return;
    initializedUserIdRef.current = userId;
    initData();
  }, [userId, initData]);

  // Load all initial data
  useEffect(() => {
    if (!urlScanLoading) {
      if (webResults?.length > 0) {
        if (!webLoading) setUrlHeader(urlDashboardView);
        loadWebData(currentIndex);
      }
    }
  }, [
    webResults,
    currentIndex,
    urlScanLoading,
    webLoading,
    urlDashboardView,
    loadWebData,
    setUrlHeader
  ]);

  // Handle redirection back to reports
  const handleReturnToDashboard = () => {
    setUrlDashboardView(true);
    setUrlHeader(true);
  };

  return (
    <div
      className="web-dashboard overflow-y-auto"
      style={{ height: bHeightVal }}
    >
      {webLoading ? (
        <Loader type="web_vul" />
      ) : (
        <>
          {!urlDashboardView ? (
            <div className="p-1 col-12">
              {/* --- ADDED REDIRECT BUTTON --- */}
              {webResults?.length > 0 && (
                <div className="d-flex justify-content-end px-4 mb-2">
                  <button
                    className="btn d-flex align-items-center gap-2 shadow-sm fw-bold"
                    onClick={handleReturnToDashboard}
                    style={{
                      backgroundColor: "#344266",
                      color: "white",
                      borderRadius: "10px",
                      fontSize: "0.85rem",
                      padding: "8px 20px"
                    }}
                  >
                    <i className="bi bi-grid-3x3-gap-fill"></i>
                    View Existing {webResults?.length > 1 ? "Reports" : "Report"} ({webResults.length})
                  </button>
                </div>
              )}

              <div className="mt-1" style={{ paddingRight: 10 }}>
                <ScanCard />
              </div>
            </div>
          ) : (
            <>
              {urlScanLoading ? (
                <div
                  className="position-absolute top-50 start-50 translate-middle"
                  style={{ width: "min(520px, 80vw)" }}
                >
                  <div className="d-flex flex-column align-items-center text-center">
                    <Loader type="web_vul" />
                    <p className="mt-3 mb-1 fw-bold text-muted">
                      {scanJob?.message || "Preparing the domain security scan..."}
                    </p>
                    <div className="progress w-100 mt-2" style={{ height: 10 }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        aria-valuenow={scanJob?.progress || 0}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        style={{ width: `${scanJob?.progress || 0}%` }}
                      />
                    </div>
                    <div className="small text-muted mt-2">
                      {scanJob?.progress || 0}% complete
                      {scanJob?.stage ? ` · ${scanJob.stage.replaceAll("-", " ")}` : ""}
                    </div>
                    {scanJob?.tools && (
                      <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                        {Object.entries(scanJob.tools).map(([tool, details]) => (
                          <span
                            key={tool}
                            className={`badge ${
                              details.status === "completed"
                                ? "bg-success"
                                : details.status === "partial"
                                  ? "bg-warning text-dark"
                                : details.status === "skipped"
                                  ? "bg-secondary"
                                  : "bg-warning text-dark"
                            }`}
                          >
                            {tool}: {details.status}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-row" style={{ paddingRight: 10 }}>
                  {/* SIDEBAR SELECTOR */}
                  <div className="d-flex flex-column mt-2" style={{ width: 76 }}>
                    {webResults.length > 0 &&
                      webResults.map((result, index) => (
                        <div
                          key={result.scan_uuid}
                          className="rounded-2 px-2 py-1 mb-2 fs-6 fw-semibold w-100 d-flex align-items-center justify-content-center"
                          onClick={() => setCurrentIndex(index)}
                          style={{
                            color: "white",
                            backgroundColor:
                              currentIndex === index
                                ? theme.labelTextDanger
                                : theme.secondary,
                            height: 34,
                            cursor: "pointer",
                            transition: "all 0.2s"
                          }}
                        >
                          S{index + 1}
                        </div>
                      ))}

                    {/* BUTTON TO ADD NEW SCAN */}
                    <div
                      onClick={() => {
                        setUrlDashboardView(false);
                        setUrlHeader(false);
                      }}
                      className="rounded-2 mb-2 fs-5 fw-bold w-100 text-center d-flex align-items-center justify-content-center"
                      style={{
                        color: theme.secondary,
                        backgroundColor: "white",
                        border: `2px dashed ${theme.disabled}`,
                        height: 34,
                        cursor: "pointer",
                      }}
                    >
                      <i className="bi bi-plus-lg"></i>
                    </div>
                  </div>

                  <ScanView />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WebDashboard;
